import { theme as dsTheme } from '@skie-io/react'
import * as echarts from 'echarts'
import { useEffect, useRef } from 'react'
import useRetryablePostJSON from '~/hooks/use_retryable_post_json'

// ECharts needs a hex; @skie-io/react 4.0.0 theme.colors.skieGray[1].
const HIGHLIGHT_BAND = dsTheme.colors.skieGray[1]

// The axis is labelled server-side with Ruby's `%b %Y`
// (patch_management_controller.rb), which is English whatever the locale.
const AXIS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function axisLabelFor(period) {
  const [year, month] = (period ?? '').split('-')
  const name = AXIS_MONTHS[Number(month) - 1]

  return name && year ? `${name} ${year}` : null
}

// The window is always the 12 months ending now, never the selected period
// (PatchManagementTrendQuery), so the selected month is marked rather than moved.
export function withHighlight(option, period) {
  const label = axisLabelFor(period)
  if (!label || !option.xAxis?.data?.includes(label)) return option

  const [first, ...rest] = option.series

  return {
    ...option,
    xAxis: {
      ...option.xAxis,
      axisLabel: {
        ...option.xAxis.axisLabel,
        formatter: (value) => (value === label ? `{selected|${value}}` : value),
        rich: { selected: { fontWeight: 'bold' } },
      },
    },
    series: [
      {
        ...first,
        markArea: {
          silent: true,
          itemStyle: { color: HIGHLIGHT_BAND },
          data: [[{ xAxis: label }, { xAxis: label }]],
        },
      },
      ...rest,
    ],
  }
}

function tooltipFormatter(params) {
  if (!params?.length) return ''
  let html = `<strong>${params[0].axisValueLabel}</strong>`
  params.forEach(({ marker, seriesName, data: d }) => {
    const count = typeof d === 'object' ? d.value : d
    html += `<br/>${marker} ${seriesName}: ${Number(count).toLocaleString()}`
  })
  return html
}

// The endpoint returns a raw ECharts option, so this cannot go through ~/components/echarts:
// its Container routes on `data.type`. Both UIs read the same endpoint.
export default function PatchTrendChart({ url, params, highlightMonth }) {
  const { data } = useRetryablePostJSON({ path: url, params })
  const divRef = useRef()
  const chartRef = useRef()

  useEffect(() => {
    const chart = echarts.init(divRef.current)
    chartRef.current = chart
    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(divRef.current)
    observer.observe(document.querySelector('body'))
    return () => {
      chart.dispose()
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!data?.series || !chartRef.current) return
    chartRef.current.clear()
    chartRef.current.setOption(
      withHighlight({ ...data, tooltip: { ...data.tooltip, formatter: tooltipFormatter } }, highlightMonth),
    )
  }, [data, highlightMonth])

  return <div ref={divRef} style={{ height: '23em', width: '100%' }} data-testid='chart-wrapper' />
}
